import { defineStore } from "pinia";
import db from "@/db";
import PARAMETERS_CATEGORIES from "@/db/enums/parametersCategories";
import crudGenerator from "@/api/utils/offlineSync";
import { useProjectsStore } from "@/store/modules/projects";
import { useInstallationsStore } from "@/store/modules/installations";

const {
  innerFetch,
  innerCreate,
  innerPartialUpdate,
  innerBulkCreate,
  innerDelete,
} = crudGenerator(db.models.ConfigurationLine);

const UNDELETABLE_PARAMETERS = new Set(["vitesse", "Débit", "Humidité"]);

const toConfigurationLineForm = (installationUuid, parameter) => ({
  installation: installationUuid,
  parameter: parameter.uuid,
  underAccreditation: parameter.underAccreditation,
  sampleCount: parameter.sampleCount,
  name: parameter.name,
  categories: parameter.categories,
});

export const useConfigurationLinesStore = defineStore("configurationLines", {
  state: () => ({
    configurationLines: {},
    configurationLineIsLoading: false,
  }),
  actions: {
    async fetchConfigurationLines(projectUuid, { installationUuid } = {}) {
      return innerFetch(
        installationUuid
          ? { installation: installationUuid }
          : { project: projectUuid },
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.configurationLineIsLoading = isLoading;
        }
      );
    },

    async setCurrentConfigurationLines(installationUuid) {
      this.configurationLineIsLoading = true;
      const lines = await db.configurationLines
        .where({ installation: installationUuid })
        .toArray();

      const configLines = {};
      for (const line of lines) {
        await line.setRelationships();
        configLines[line.parameterObject.uuid] = line;
      }
      this.configurationLines = configLines;
      this.configurationLineIsLoading = false;
    },

    async createConfigurationLine({ installationUuid, parameter }) {
      const configLine = await innerCreate(
        {
          installation: installationUuid,
          parameter: parameter.uuid,
          underAccreditation: parameter.underAccreditation,
          sampleCount: parameter.sampleCount,
        },
        useProjectsStore().isOfflineState
      );
      useInstallationsStore().refreshLastUpdated();

      this.upsertConfigurationLine(configLine);

      return configLine;
    },

    async fetchSumSelectionParameter({ installationUuid, parameterUuidList }) {
      const uuids = Object.keys(this.configurationLines);
      const params = await db.parameters
        .filter(
          (parameter) =>
            parameterUuidList.includes(parameter.uuid) &&
            !uuids.includes(parameter.uuid)
        )
        .toArray();
      return params.map((parameter) =>
        toConfigurationLineForm(installationUuid, parameter)
      );
    },

    async fetchPreselectionParameter({ installationUuid, preselection }) {
      const uuids = Object.keys(this.configurationLines);
      const params = await db.parameters
        .filter(
          (parameter) =>
            parameter.preselections.includes(preselection) &&
            !parameter.mutualisations.length &&
            !parameter.categories.includes(PARAMETERS_CATEGORIES.MTX) &&
            !uuids.includes(parameter.uuid)
        )
        .toArray();

      return params.map((parameter) =>
        toConfigurationLineForm(installationUuid, parameter)
      );
    },

    async bulkCreateConfigurationLine({ form }) {
      const configurationLines = await innerBulkCreate(
        form,
        useProjectsStore().isOfflineState
      );
      for (const line of configurationLines) {
        this.configurationLines[line.parameterObject.uuid] = line;
      }
      this.configurationLines = { ...this.configurationLines };
      useInstallationsStore().refreshLastUpdated();
      return configurationLines;
    },

    async updateConfigurationLine(form) {
      const configLine = await innerPartialUpdate(
        form,
        useProjectsStore().isOfflineState
      );
      useInstallationsStore().refreshLastUpdated();
      this.upsertConfigurationLine(configLine);
      return configLine;
    },

    async deleteConfigurationLine(configurationLine) {
      if (!configurationLine.parameterObject) {
        await configurationLine.setRelationships();
      }
      if (UNDELETABLE_PARAMETERS.has(configurationLine.parameterObject?.name))
        return;
      const projectsStore = useProjectsStore();
      await innerDelete(
        configurationLine.uuid,
        projectsStore.isOfflineState,
        projectsStore.currentProjectUuid
      );
      const configurationLines = { ...this.configurationLines };
      delete configurationLines[configurationLine.parameterObject.uuid];
      this.configurationLines = configurationLines;
      useInstallationsStore().refreshLastUpdated();
    },

    async getConfigurationLinesParametersIds(installationUuid) {
      const lines = await db.configurationLines
        .where({ installation: installationUuid })
        .toArray();

      return lines.map((item) => item.parameter);
    },

    upsertConfigurationLine(configurationLine) {
      this.configurationLines = {
        ...this.configurationLines,
        [configurationLine.parameterObject.uuid]: configurationLine,
      };
    },
  },
});
